import { useEffect, useRef } from "react";

const MAX_COLS: Record<string, number> = { check: 6, text: 4 };

interface Cell {
    cellType: "text" | "checkbox";
    value: any;
}

interface Row {
    cells: Cell[];
}

interface TableData {
    tableType: string;
    headers: string[];
    rows: Row[];
    columnKeys?: string[];
}

interface Section {
    id: number;
    type?: string;
    subtitle: string;
    tableData?: TableData;
}

interface TableSectionProps {
    section: Section;
    isViewMode: boolean;
    index: number;
    totalCount: number;
    onHeaderChange: (sectionId: number, target: string, value: string, colIdx?: number) => void;
    onCellChange: (sectionId: number, rIdx: number, cIdx: number, value: any) => void;
    onAddRow: (sectionId: number) => void;
    onRemoveRow: (sectionId: number, rIdx: number) => void;
    onAddCol: (sectionId: number) => void;
    onRemoveCol: (sectionId: number) => void;
    onRemoveSection: (id: number) => void;
    onMoveSection?: (index: number, direction: "up" | "down") => void;
    dragHandleProps?: any;
}

export default function TableSection({
    section,
    isViewMode,
    index,
    totalCount,
    onHeaderChange,
    onCellChange,
    onAddRow,
    onRemoveRow,
    onAddCol,
    onRemoveCol,
    onRemoveSection,
    onMoveSection,
    dragHandleProps,
}: TableSectionProps) {
    const { headers, rows, tableType } = section.tableData!;
    const isCheckType = tableType === "check";
    const maxCols = MAX_COLS[tableType] ?? 4;
    const isColMax = headers.length >= maxCols;
    const textareaRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});

    useEffect(() => {
        Object.values(textareaRefs.current).forEach(el => {
            if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; }
        });
    }, [rows]);

    return (
        <div className="flex items-start gap-2 mb-2">
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                    <span className={`h-5 px-2 rounded text-white text-[11px] font-semibold flex items-center ${isCheckType ? "bg-[#887267]" : "bg-[#b8a39b]"}`}>
                        {isCheckType ? "체크용" : "텍스트용"}
                    </span>
                    {!isViewMode && <span className="text-[11px] text-gray-400">{headers.length}/{maxCols}열</span>}
                </div>

                <div className="overflow-x-auto">
                    <table className="border-collapse text-sm w-full">
                        <thead>
                            <tr>
                                {headers.map((h, ci) => (
                                    <th key={ci} className="border border-[#e8e0dc] bg-[#f5f2f0] p-0 min-w-[60px]">
                                        <input type="text" value={h} disabled={isViewMode}
                                            onChange={(e) => onHeaderChange(section.id, "header", e.target.value, ci)}
                                            className={`w-full h-8 px-2 text-[#2a1f1a] text-[12px] font-semibold text-center focus:outline-none bg-transparent ${isViewMode ? "cursor-default" : "focus:bg-[#fdf8f0]"}`}
                                        />
                                    </th>
                                ))}
                                {!isViewMode && <th className="border-none w-6" />}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, ri) => (
                                <tr key={ri}>
                                    {row.cells.map((cell, ci) => (
                                        <td key={ci} className="border border-[#e8e0dc] p-0 min-w-[60px]">
                                            <div className="flex items-center justify-center min-h-[2rem] px-1 py-1">
                                                {isCheckType && ci > 0 ? (
                                                    isViewMode ? (
                                                        <span className={`text-base font-bold ${cell.value ? "text-[#887267]" : "text-[#e8e0dc]"}`}>{cell.value ? "✓" : ""}</span>
                                                    ) : (
                                                        <input type="checkbox" checked={cell.value === "O"}
                                                            onChange={(e) => onCellChange(section.id, ri, ci, e.target.checked ? "O" : "X")}
                                                            className="w-4 h-4 accent-[#887267] cursor-pointer"
                                                        />
                                                    )
                                                ) : (
                                                    <textarea
                                                        ref={el => { textareaRefs.current[`${ri}-${ci}`] = el; }}
                                                        value={cell.value} disabled={isViewMode} rows={1}
                                                        onChange={(e) => { onCellChange(section.id, ri, ci, e.target.value); e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }}
                                                        className={`w-full px-1 text-[#2a1f1a] text-[12px] resize-none overflow-hidden whitespace-pre-wrap ${!isCheckType ? "text-center" : ""} focus:outline-none ${isViewMode ? "bg-transparent cursor-default" : "bg-white focus:bg-[#fdf8f0]"}`}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    ))}
                                    {!isViewMode && (
                                        <td className="border-none pl-1">
                                            <button type="button" onClick={() => onRemoveRow(section.id, ri)} disabled={rows.length <= 1}
                                                className="w-5 h-5 rounded-full bg-red-400 flex items-center justify-center disabled:opacity-30 hover:bg-red-500">
                                                <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12" /></svg>
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {!isViewMode && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                        <button type="button" onClick={() => onAddRow(section.id)}
                            className="h-7 px-3 border border-[#887267] text-[#887267] rounded-lg text-[11px] font-medium hover:bg-[#f5f2f0] transition-colors flex items-center gap-1">
                            <svg className="w-2.5 h-2.5" viewBox="0 0 10 10" fill="currentColor"><path d="M10 6H6V10H4V6H0V4H4V0H6V4H10V6Z" /></svg>
                            행 추가
                        </button>
                        <button type="button" onClick={() => onAddCol(section.id)} disabled={isColMax}
                            className="h-7 px-3 border border-[#887267] text-[#887267] rounded-lg text-[11px] font-medium hover:bg-[#f5f2f0] transition-colors flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                            <svg className="w-2.5 h-2.5" viewBox="0 0 10 10" fill="currentColor"><path d="M10 6H6V10H4V6H0V4H4V0H6V4H10V6Z" /></svg>
                            열 추가
                        </button>
                        <button type="button" onClick={() => onRemoveCol(section.id)} disabled={headers.length <= 1}
                            className="h-7 px-3 border border-red-200 text-red-400 rounded-lg text-[11px] font-medium hover:bg-red-50 transition-colors disabled:opacity-30">
                            열 삭제
                        </button>
                    </div>
                )}
            </div>

            {!isViewMode && (
                <div className="flex flex-col gap-1 shrink-0 items-center">
                    <button type="button" onClick={() => onRemoveSection(section.id)}
                        className="w-7 h-7 rounded-full bg-[#887267] flex items-center justify-center hover:bg-[#6b5248]">
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                    {dragHandleProps && (
                        <div {...dragHandleProps} className="flex flex-col gap-0.5 p-2 cursor-grab active:cursor-grabbing text-[#b8a39b]">
                            {[1,2,3].map(i => <div key={i} className="w-4 h-0.5 bg-current"></div>)}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}